import React from 'react';
import { createPortal } from 'react-dom';
import { withAgentCompanyQuery } from '../agentCompanyQuery';
import DodTermText from '../DodTermText';
import { requestLocalAgentPickFolder } from '../localAgentPickFolder';
import EvidenceSourcesPanel from './EvidenceSourcesPanel';
import './AgentSettingsPanel.css';

type FeatureKey =
  | 'cloud-compliance'
  | 'dod-contracts-compliance'
  | 'ofac-compliance'
  | 'website-compliance'
  | 'business-creation-compliance';

type MonitorRun = {
  run_id: string;
  skill: string;
  status: string;
  title?: string;
  summary?: string;
  draft?: {
    kind?: string;
    openTask?: string;
    proposals?: Array<{
      id?: string;
      title?: string;
      action?: string;
      method?: string;
      confidence?: number;
    }>;
    findings?: Array<{ id?: string; label?: string; state?: string; note?: string }>;
  };
};

type PermissionCatalogEntry = {
  id: string;
  label_en?: string;
  label_es?: string;
  modes?: string[];
};

type ScanRoot = {
  id?: string;
  path: string;
  os?: string;
  recursive?: boolean;
  preset?: boolean;
  label_en?: string;
  label_es?: string;
};

type SchedulePayload = {
  checkin_cadence?: string;
  discovery_cadence?: string;
  solution_scan_cadence?: string;
  solution_scan?: { note_en?: string; note_es?: string };
  checkin?: { note_en?: string; note_es?: string };
  discovery?: { note_en?: string; note_es?: string };
  scan_roots?: ScanRoot[];
  scan_root_presets?: ScanRoot[];
  presets?: {
    solution_scan?: Array<{ id: string; label_en?: string; label_es?: string }>;
    checkin?: Array<{ id: string; label_en?: string; label_es?: string }>;
    discovery?: Array<{ id: string; label_en?: string; label_es?: string }>;
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  feature: FeatureKey;
  language: 'en' | 'es';
  gatewayBaseUrl: string;
  accessToken: string;
  /** Console account picker — scopes schedule / Sources to that company. */
  companyId?: number | null;
  readOnly?: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Open scrolled to drafts when the gap-draft chip was clicked. */
  focusSection?: 'drafts' | 'schedule' | 'sources';
  onOpenTask?: (controlId: string) => void;
};

const FEATURE_SKILL: Record<FeatureKey, string> = {
  'cloud-compliance': 'cloud_review',
  'dod-contracts-compliance': 'dod_gap',
  'ofac-compliance': 'ofac_watch',
  'website-compliance': 'website_scan',
  'business-creation-compliance': 'fleet_discovery',
};

/**
 * Agent cog panel: schedule/folders, gates, install.
 * Sources focus = connect-only proof places. Drafts focus = approval queue.
 * Smart scan / Find proof stay on the task agent strip (not duplicated here).
 * Opens from AgentRunBadges — does not replace FeatureAgentGates.
 */
export default function AgentSettingsPanel({
  open,
  onClose,
  feature,
  language,
  gatewayBaseUrl,
  accessToken,
  companyId = null,
  readOnly = false,
  anchorRef,
  focusSection = 'schedule',
  onOpenTask,
}: Props): React.ReactElement | null {
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const skill = FEATURE_SKILL[feature];
  const es = language === 'es';
  const panelRef = React.useRef<HTMLDivElement>(null);
  const draftsRef = React.useRef<HTMLElement | null>(null);
  const sourcesRef = React.useRef<HTMLElement | null>(null);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [awaiting, setAwaiting] = React.useState<MonitorRun[]>([]);
  const [schedule, setSchedule] = React.useState<SchedulePayload | null>(null);
  const [checkinCadence, setCheckinCadence] = React.useState('every_4h');
  const [discoveryCadence, setDiscoveryCadence] = React.useState('with_checkin');
  const [solutionCadence, setSolutionCadence] = React.useState('every_4h');
  const [scanRoots, setScanRoots] = React.useState<ScanRoot[]>([]);
  const [scanPresets, setScanPresets] = React.useState<ScanRoot[]>([]);
  const [customRoot, setCustomRoot] = React.useState('');
  const [permissions, setPermissions] = React.useState<Record<string, string>>({});
  const [catalog, setCatalog] = React.useState<PermissionCatalogEntry[]>([]);
  const [gatesOpen, setGatesOpen] = React.useState(false);
  const [browseBusy, setBrowseBusy] = React.useState(false);
  const [foldersDirty, setFoldersDirty] = React.useState(false);
  const scanRootsRef = React.useRef<ScanRoot[]>([]);
  React.useEffect(() => {
    scanRootsRef.current = scanRoots;
  }, [scanRoots]);

  const copy = es
    ? {
      title: 'Ajustes del agente',
      sourcesTitle: 'Fuentes de prueba',
      draftsPanelTitle: 'Borradores en espera',
      close: 'Cerrar',
      help: 'Un agente por equipo. El RBAC de proyectos decide qué puede abrir cada persona. El agente busca pruebas y propone; nada se marca hecho sin su aprobación.',
      sourcesHelp: 'Conecte dónde puede vivir la prueba. La búsqueda, el escaneo inteligente y los borradores están en la franja del agente de la tarea.',
      scheduleTitle: 'Horario y carpetas',
      scheduleHelp: 'Predeterminado: check-in y escaneo de soluciones cada 4 horas; descubrimiento de nombres con cada check-in. Elija carpetas abajo (nombres y rutas solo).',
      checkinCadence: 'Frecuencia de check-in',
      discoveryCadence: 'Frecuencia de descubrimiento de carpetas',
      foldersTitle: 'Carpetas a escanear',
      foldersHelp: 'Presets del sistema (incl. ~/CODE). Use Examinar para elegir una carpeta en este Mac, o escríbala y pulse Añadir. Los cambios se guardan al instante. El agente no lee el contenido de los archivos.',
      selectAllFolders: 'Seleccionar todas',
      clearAllFolders: 'Quitar todas',
      addFolder: 'Añadir',
      browseFolder: 'Examinar…',
      browsingFolder: 'Eligiendo…',
      folderPlaceholder: '~/CODE',
      foldersSaved: 'Carpetas guardadas.',
      foldersUnsaved: 'Cambios locales — guardando…',
      browseNeedAgent: 'Examinar necesita el agente en este equipo. Actualice/instale el agente, o escriba la ruta (p. ej. ~/CODE).',
      smartTitle: 'Escaneo inteligente',
      smartHelp: 'Con IA: revisa hallazgos, nombres descubiertos y el portafolio, y propone pasos. Cadencia abajo.',
      smartRun: 'Ejecutar escaneo inteligente ahora',
      reviewRun: 'Ejecutar revisión ahora',
      running: 'Ejecutando…',
      cadence: 'Cadencia de soluciones',
      saveCadence: 'Guardar horario y carpetas',
      drafts: 'Borradores en espera',
      draftsEmpty: 'Nada espera una decisión.',
      draftsHelp: 'Estos borradores no cambian el checklist solos. Ábralos, apruébelos o vaya al paso.',
      openTask: 'Abrir ese paso',
      approve: 'Aprobar',
      reject: 'Rechazar',
      dismiss: 'Descartar',
      gates: 'Puertas humanas',
      saveGates: 'Guardar puertas',
      modeOff: 'Apagado',
      modeApproval: 'Tras aprobación',
      modeAuto: 'Automático',
      install: 'Instalar / dispositivos',
      scope: 'Alcance completo',
      proposals: 'Propuestas',
      loadError: 'No se pudo cargar el agente.',
      saveError: 'No se pudo guardar.',
      runError: 'No se pudo ejecutar.',
      decideError: 'No se pudo registrar la decisión.',
      smartOk: 'Escaneo listo. Revise borradores abajo.',
      saved: 'Guardado.',
    }
    : {
      title: 'Agent settings',
      sourcesTitle: 'Proof sources',
      draftsPanelTitle: 'Drafts waiting',
      close: 'Close',
      help: 'One agent per computer. Project RBAC decides what each person can open. The agent scans for proof and proposes fixes; nothing is marked done without your approval.',
      sourcesHelp: 'Connect where proof can live. Search, smart scan, and drafts stay on the task agent strip.',
      scheduleTitle: 'Schedule and folders',
      scheduleHelp: 'Default: check-in and solution scan every 4 hours; folder-name discovery with each check-in. Pick folders below (names and paths only).',
      checkinCadence: 'Check-in frequency',
      discoveryCadence: 'Folder discovery frequency',
      foldersTitle: 'Folders to scan',
      foldersHelp: 'System presets (including ~/CODE). Use Browse to pick a folder on this computer, or type a path and press Add. Changes save immediately. The agent does not read file contents.',
      selectAllFolders: 'Select all',
      clearAllFolders: 'Clear all',
      addFolder: 'Add',
      browseFolder: 'Browse…',
      browsingFolder: 'Choosing…',
      folderPlaceholder: '~/CODE',
      foldersSaved: 'Folders saved.',
      foldersUnsaved: 'Local changes — saving…',
      browseNeedAgent: 'Browse needs the agent on this computer. Update/install the agent, or type the path (e.g. ~/CODE).',
      smartTitle: 'Smart scan',
      smartHelp: 'Uses AI to review findings, discovered names, and portfolio signals, then proposes next steps. Set cadence below.',
      smartRun: 'Run smart scan now',
      reviewRun: 'Run review now',
      running: 'Running…',
      cadence: 'Solution scan cadence',
      saveCadence: 'Save schedule and folders',
      drafts: 'Drafts waiting',
      draftsEmpty: 'Nothing is waiting on a decision.',
      draftsHelp: 'These drafts do not change the checklist by themselves. Open them, approve them, or go to the step.',
      openTask: 'Open that step',
      approve: 'Approve',
      reject: 'Reject',
      dismiss: 'Dismiss',
      gates: 'Human gates',
      saveGates: 'Save gates',
      modeOff: 'Off',
      modeApproval: 'After approval',
      modeAuto: 'Automatic',
      install: 'Install / devices',
      scope: 'Full data scope',
      proposals: 'Proposals',
      loadError: 'Could not load the agent.',
      saveError: 'Could not save.',
      runError: 'Could not run.',
      decideError: 'Could not record the decision.',
      smartOk: 'Scan ready. Review drafts below.',
      saved: 'Saved.',
    };

  const load = React.useCallback(async () => {
    if (!open || !accessToken || !gateway) return;
    try {
      const [monitorRes, scheduleRes, permRes] = await Promise.all([
        fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/monitor', companyId)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/schedule', companyId)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(
          `${gateway}${withAgentCompanyQuery(`/api/v1/agent/monitor/permissions?feature=${encodeURIComponent(feature)}`, companyId)}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        ),
      ]);
      if (monitorRes.ok) {
        const body = await monitorRes.json() as { awaiting?: MonitorRun[] };
        setAwaiting(
          (Array.isArray(body.awaiting) ? body.awaiting : []).filter((run) => run.skill === skill),
        );
      }
      if (scheduleRes.ok) {
        const body = await scheduleRes.json() as SchedulePayload;
        setSchedule(body);
        setCheckinCadence(body.checkin_cadence || 'every_4h');
        setDiscoveryCadence(body.discovery_cadence || 'with_checkin');
        setSolutionCadence(body.solution_scan_cadence || 'every_4h');
        setScanRoots(Array.isArray(body.scan_roots) ? body.scan_roots : []);
        setScanPresets(Array.isArray(body.scan_root_presets) ? body.scan_root_presets : []);
      }
      if (permRes.ok) {
        const body = await permRes.json() as {
          permissions?: Record<string, string>;
          catalog?: PermissionCatalogEntry[];
        };
        setPermissions(body.permissions && typeof body.permissions === 'object' ? body.permissions : {});
        setCatalog(Array.isArray(body.catalog) ? body.catalog : []);
      }
      setError('');
    } catch {
      setError(copy.loadError);
    }
  }, [accessToken, companyId, copy.loadError, feature, gateway, open, skill]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (!open) return undefined;
    if (focusSection === 'drafts') {
      const timer = window.setTimeout(() => {
        draftsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 40);
      return () => window.clearTimeout(timer);
    }
    if (focusSection === 'sources' || focusSection === 'schedule') {
      const timer = window.setTimeout(() => {
        sourcesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 40);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [focusSection, open, awaiting.length]);

  // useLayoutEffect so fixed coords exist before paint — without top/left a
  // portaled `position:fixed` panel lands at the bottom of the document.
  React.useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }
    const place = () => {
      if (typeof window === 'undefined') return;
      const anchor = anchorRef?.current;
      const panel = panelRef.current;
      const width = Math.min(28 * 16, window.innerWidth - 24);
      const maxHeight = Math.min(window.innerHeight * 0.72, 36 * 16);
      if (!anchor) {
        setPanelStyle({
          position: 'fixed',
          top: 72,
          right: 16,
          left: 'auto',
          width,
          maxHeight,
        });
        return;
      }
      const rect = anchor.getBoundingClientRect();
      let left = rect.right - width;
      if (left < 12) left = 12;
      if (left + width > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - width - 12);
      }
      const measured = panel?.offsetHeight || 0;
      const height = measured > 0 ? Math.min(measured, maxHeight) : Math.min(360, maxHeight);
      let top = rect.bottom + 8;
      const roomBelow = window.innerHeight - 12 - top;
      if (height > roomBelow) {
        const above = rect.top - 8 - height;
        top = above >= 12 ? above : Math.max(12, window.innerHeight - height - 12);
      }
      setPanelStyle({
        position: 'fixed',
        top,
        left,
        width,
        maxHeight,
        right: 'auto',
      });
    };
    place();
    const raf = window.requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // Defer outside-click so the cog click that opened the panel does not instantly close it.
    let removePointer: (() => void) | undefined;
    const timer = window.setTimeout(() => {
      const onPointer = (event: MouseEvent) => {
        const panel = panelRef.current;
        const anchor = anchorRef?.current;
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (panel?.contains(target)) return;
        if (anchor?.contains(target)) return;
        onClose();
      };
      window.addEventListener('mousedown', onPointer);
      removePointer = () => window.removeEventListener('mousedown', onPointer);
    }, 0);
    window.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      removePointer?.();
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, onClose, open]);

  const startSmartScan = async () => {
    if (readOnly || !accessToken) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const path = skill === 'fleet_discovery'
        ? '/api/v1/agent/monitor/runs'
        : '/api/v1/agent/monitor/smart-scan';
      const response = await fetch(`${gateway}${withAgentCompanyQuery(path, companyId)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(skill === 'fleet_discovery' ? { skill } : { skill, force: true }),
      });
      const body = await response.json() as { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : copy.runError);
        return;
      }
      setMessage(copy.smartOk);
      await load();
    } catch {
      setError(copy.runError);
    } finally {
      setBusy(false);
    }
  };

  const startReview = async () => {
    if (readOnly || !accessToken) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/monitor/runs', companyId)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ skill }),
      });
      const body = await response.json() as { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : copy.runError);
        return;
      }
      setMessage(copy.smartOk);
      await load();
    } catch {
      setError(copy.runError);
    } finally {
      setBusy(false);
    }
  };

  const persistSchedule = React.useCallback(async (
    nextRoots: ScanRoot[],
    options?: { quietFolders?: boolean },
  ) => {
    const quietFolders = Boolean(options?.quietFolders);
    if (readOnly || !accessToken) return false;
    setBusy(true);
    setError('');
    if (!quietFolders) setMessage('');
    try {
      const response = await fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/schedule', companyId)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(companyId ? { company_id: companyId } : {}),
          solution_scan_cadence: solutionCadence,
          checkin_cadence: checkinCadence,
          discovery_cadence: discoveryCadence,
          scan_roots: nextRoots.map((row) => ({
            id: row.id || '',
            path: row.path,
            os: row.os || 'any',
            recursive: row.recursive !== false,
            preset: Boolean(row.preset),
          })),
        }),
      });
      const body = await response.json() as SchedulePayload & { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : copy.saveError);
        setFoldersDirty(true);
        return false;
      }
      setSchedule(body);
      setCheckinCadence(body.checkin_cadence || checkinCadence);
      setDiscoveryCadence(body.discovery_cadence || discoveryCadence);
      setSolutionCadence(body.solution_scan_cadence || solutionCadence);
      if (Array.isArray(body.scan_roots)) setScanRoots(body.scan_roots);
      if (Array.isArray(body.scan_root_presets)) setScanPresets(body.scan_root_presets);
      setFoldersDirty(false);
      setMessage(quietFolders ? copy.foldersSaved : copy.saved);
      return true;
    } catch {
      setError(copy.saveError);
      setFoldersDirty(true);
      return false;
    } finally {
      setBusy(false);
    }
  }, [
    accessToken,
    checkinCadence,
    companyId,
    copy.foldersSaved,
    copy.saveError,
    copy.saved,
    discoveryCadence,
    gateway,
    readOnly,
    solutionCadence,
  ]);

  const saveCadence = async () => {
    await persistSchedule(scanRootsRef.current);
  };

  const saveGates = async () => {
    if (readOnly || !accessToken) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const owned = Object.fromEntries(
        catalog.map((entry) => [entry.id, permissions[entry.id] || 'off']),
      );
      const response = await fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/monitor/permissions', companyId)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ permissions: owned }),
      });
      const body = await response.json() as { permissions?: Record<string, string>; detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : copy.saveError);
        return;
      }
      if (body.permissions) setPermissions(body.permissions);
      setMessage(copy.saved);
    } catch {
      setError(copy.saveError);
    } finally {
      setBusy(false);
    }
  };

  const decide = async (runId: string, decision: 'approved' | 'rejected' | 'dismissed') => {
    if (readOnly || !accessToken) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(
        `${gateway}${withAgentCompanyQuery(`/api/v1/agent/monitor/runs/${encodeURIComponent(runId)}/decide`, companyId)}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ decision }),
        },
      );
      const body = await response.json() as { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : copy.decideError);
        return;
      }
      setMessage(
        decision === 'approved'
          ? (es ? 'Aprobado.' : 'Approved.')
          : decision === 'rejected'
            ? (es ? 'Rechazado.' : 'Rejected.')
            : (es ? 'Descartado.' : 'Dismissed.'),
      );
      await load();
    } catch {
      setError(copy.decideError);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const checkinOptions = schedule?.presets?.checkin?.length
    ? schedule.presets.checkin
    : [
      { id: 'manual', label_en: 'Manual only', label_es: 'Solo manual' },
      { id: 'every_4h', label_en: 'Every 4 hours (recommended)', label_es: 'Cada 4 horas (recomendado)' },
      { id: 'daily', label_en: 'Daily', label_es: 'Diario' },
      { id: 'weekly', label_en: 'Weekly', label_es: 'Semanal' },
    ];
  const discoveryOptions = schedule?.presets?.discovery?.length
    ? schedule.presets.discovery
    : [
      { id: 'manual', label_en: 'Manual only', label_es: 'Solo manual' },
      { id: 'with_checkin', label_en: 'With each check-in (recommended)', label_es: 'Con cada check-in (recomendado)' },
      { id: 'every_4h', label_en: 'Every 4 hours', label_es: 'Cada 4 horas' },
      { id: 'daily', label_en: 'Daily', label_es: 'Diario' },
      { id: 'weekly', label_en: 'Weekly', label_es: 'Semanal' },
    ];
  const cadenceOptions = schedule?.presets?.solution_scan?.length
    ? schedule.presets.solution_scan
    : [
      { id: 'off', label_en: 'Off', label_es: 'Apagado' },
      { id: 'every_4h', label_en: 'Every 4 hours (recommended)', label_es: 'Cada 4 horas (recomendado)' },
      { id: 'with_checkin', label_en: 'With check-in', label_es: 'Con check-in' },
      { id: 'daily', label_en: 'Daily', label_es: 'Diario' },
      { id: 'weekly', label_en: 'Weekly', label_es: 'Semanal' },
    ];

  const rootPaths = new Set(scanRoots.map((row) => String(row.path || '').trim().toLowerCase()));
  const defaultFolderPresets: ScanRoot[] = [
    { id: 'documents', path: '~/Documents', label_en: 'User Documents', label_es: 'Documentos' },
    { id: 'downloads', path: '~/Downloads', label_en: 'Downloads', label_es: 'Descargas' },
    { id: 'dropbox_home', path: '~/Dropbox', label_en: 'Dropbox', label_es: 'Dropbox' },
    { id: 'mac_cloudstorage', path: '~/Library/CloudStorage', label_en: 'macOS CloudStorage', label_es: 'CloudStorage macOS' },
  ];
  const folderPresets = scanPresets.length ? scanPresets : defaultFolderPresets;
  const customFolders = scanRoots.filter((row) => (
    !folderPresets.some((preset) => String(preset.path || '').trim().toLowerCase() === String(row.path || '').trim().toLowerCase())
  ));
  const allFolderKeys = [
    ...folderPresets.map((row) => String(row.path || '').trim().toLowerCase()),
    ...customFolders.map((row) => String(row.path || '').trim().toLowerCase()),
  ].filter(Boolean);
  const allFoldersSelected = allFolderKeys.length > 0 && allFolderKeys.every((key) => rootPaths.has(key));
  const someFoldersSelected = allFolderKeys.some((key) => rootPaths.has(key));
  const commitRoots = (next: ScanRoot[]) => {
    setScanRoots(next);
    scanRootsRef.current = next;
    setFoldersDirty(true);
    setMessage(copy.foldersUnsaved);
    void persistSchedule(next, { quietFolders: true });
  };
  const toggleAllFolders = () => {
    if (allFoldersSelected) {
      commitRoots([]);
      return;
    }
    const byKey = new Map<string, ScanRoot>();
    for (const preset of folderPresets) {
      const path = String(preset.path || '').trim();
      if (!path) continue;
      byKey.set(path.toLowerCase(), {
        id: preset.id || '',
        path,
        os: preset.os || 'any',
        recursive: preset.recursive !== false,
        preset: true,
        label_en: preset.label_en,
        label_es: preset.label_es,
      });
    }
    for (const row of customFolders) {
      const path = String(row.path || '').trim();
      if (!path) continue;
      byKey.set(path.toLowerCase(), row);
    }
    commitRoots(Array.from(byKey.values()));
  };
  const togglePreset = (preset: ScanRoot) => {
    const path = String(preset.path || '').trim();
    if (!path) return;
    const key = path.toLowerCase();
    if (rootPaths.has(key)) {
      commitRoots(scanRoots.filter((row) => String(row.path || '').trim().toLowerCase() !== key));
      return;
    }
    commitRoots([
      ...scanRoots,
      {
        id: preset.id || '',
        path,
        os: preset.os || 'any',
        recursive: preset.recursive !== false,
        preset: true,
        label_en: preset.label_en,
        label_es: preset.label_es,
      },
    ]);
  };
  const addCustomFolder = (rawPath?: string) => {
    const path = String(rawPath || customRoot).trim();
    if (!path || rootPaths.has(path.toLowerCase())) {
      if (path && rootPaths.has(path.toLowerCase())) {
        setMessage(copy.foldersSaved);
      }
      return;
    }
    commitRoots([
      ...scanRoots,
      { id: '', path, os: 'any', recursive: true, preset: false },
    ]);
    setCustomRoot('');
  };
  const browseFolder = async () => {
    if (readOnly || browseBusy) return;
    setBrowseBusy(true);
    setError('');
    try {
      const result = await requestLocalAgentPickFolder();
      if (result.ok) {
        addCustomFolder(result.path);
        return;
      }
      if (result.cancelled) return;
      setError(result.missing ? copy.browseNeedAgent : result.error);
    } finally {
      setBrowseBusy(false);
    }
  };

  const sourcesOnly = focusSection === 'sources';
  const draftsOnly = focusSection === 'drafts';
  const showSchedule = !sourcesOnly && !draftsOnly;
  const panelTitle = sourcesOnly
    ? copy.sourcesTitle
    : draftsOnly
      ? copy.draftsPanelTitle
      : copy.title;
  const panelHelp = sourcesOnly ? copy.sourcesHelp : copy.help;

  const panel = (
    <div
      className={`agent-settings-panel is-portal${sourcesOnly ? ' is-sources' : ''}${draftsOnly ? ' is-drafts' : ''}`}
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-settings-panel-title"
      // Hide until coords exist — fixed without top/left pins to the document bottom.
      style={panelStyle || {
        position: 'fixed',
        top: 0,
        left: 0,
        visibility: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <div className="agent-settings-panel__head">
        <h3 id="agent-settings-panel-title">{panelTitle}</h3>
        <button type="button" className="agent-settings-panel__close" onClick={onClose} aria-label={copy.close}>
          ×
        </button>
      </div>
      <p className="agent-settings-panel__help">
        <DodTermText text={panelHelp} language={language} seen={new Set()} />
      </p>
      {error ? <p className="agent-settings-panel__error" role="alert">{error}</p> : null}
      {message ? <p className="agent-settings-panel__ok" role="status">{message}</p> : null}

      {sourcesOnly ? (
        <section
          className="agent-settings-panel__block is-focus"
          aria-labelledby="evidence-sources-title"
          ref={(node) => { sourcesRef.current = node; }}
        >
          <EvidenceSourcesPanel
            gatewayBaseUrl={gatewayBaseUrl}
            accessToken={accessToken}
            language={language}
          />
          <div className="agent-settings-panel__folders" role="group" aria-labelledby="agent-folders-title">
            <strong id="agent-folders-title">{copy.foldersTitle}</strong>
            <p className="agent-settings-panel__help">{copy.foldersHelp}</p>
            <label className="agent-settings-panel__folder-row agent-settings-panel__folder-all">
              <input
                type="checkbox"
                checked={allFoldersSelected}
                ref={(node) => {
                  if (node) node.indeterminate = someFoldersSelected && !allFoldersSelected;
                }}
                disabled={readOnly || busy || allFolderKeys.length === 0}
                onChange={() => toggleAllFolders()}
              />
              <span>
                <strong>{allFoldersSelected ? copy.clearAllFolders : copy.selectAllFolders}</strong>
              </span>
            </label>
            <ul className="agent-settings-panel__folder-list">
              {folderPresets.map((preset) => {
                const checked = rootPaths.has(String(preset.path || '').trim().toLowerCase());
                const label = es
                  ? (preset.label_es || preset.label_en || preset.path)
                  : (preset.label_en || preset.label_es || preset.path);
                return (
                  <li key={preset.id || preset.path}>
                    <label className="agent-settings-panel__folder-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={readOnly || busy}
                        onChange={() => togglePreset(preset)}
                      />
                      <span>
                        <strong>{label}</strong>
                        <em>{preset.path}</em>
                      </span>
                    </label>
                  </li>
                );
              })}
              {customFolders.map((row) => (
                  <li key={`custom-${row.path}`}>
                    <label className="agent-settings-panel__folder-row">
                      <input
                        type="checkbox"
                        checked
                        disabled={readOnly || busy}
                        onChange={() => togglePreset(row)}
                      />
                      <span>
                        <strong>{row.path}</strong>
                        <em>{es ? 'Personalizada' : 'Custom'}</em>
                      </span>
                    </label>
                  </li>
                ))}
            </ul>
            {!readOnly ? (
              <div className="agent-settings-panel__folder-add">
                <input
                  type="text"
                  value={customRoot}
                  disabled={busy || browseBusy}
                  placeholder={copy.folderPlaceholder}
                  aria-label={copy.foldersTitle}
                  onChange={(event) => setCustomRoot(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addCustomFolder();
                    }
                  }}
                />
                <button
                  type="button"
                  className="dashboard-btn"
                  disabled={busy || browseBusy || !customRoot.trim()}
                  onClick={() => addCustomFolder()}
                >
                  {copy.addFolder}
                </button>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  disabled={busy || browseBusy}
                  title={copy.browseNeedAgent}
                  onClick={() => void browseFolder()}
                >
                  {browseBusy ? copy.browsingFolder : copy.browseFolder}
                </button>
              </div>
            ) : null}
            {foldersDirty ? (
              <p className="agent-settings-panel__folders-dirty" role="status">
                {copy.foldersUnsaved}
              </p>
            ) : null}
          </div>
          <div className="agent-settings-panel__links">
            <a className="dashboard-btn" href="/console/agent">{copy.install}</a>
          </div>
        </section>
      ) : null}

      {showSchedule ? (
      <section className="agent-settings-panel__block" aria-labelledby="agent-schedule-title">
        <h4 id="agent-schedule-title">{copy.scheduleTitle}</h4>
        <p className="agent-settings-panel__help">
          <DodTermText text={copy.scheduleHelp} language={language} seen={new Set()} />
        </p>
        <label className="agent-settings-panel__field">
          <span>{copy.checkinCadence}</span>
          <select
            value={checkinCadence}
            disabled={readOnly || busy}
            onChange={(event) => setCheckinCadence(event.target.value)}
          >
            {checkinOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {es ? (opt.label_es || opt.id) : (opt.label_en || opt.id)}
              </option>
            ))}
          </select>
        </label>
        <label className="agent-settings-panel__field">
          <span>{copy.discoveryCadence}</span>
          <select
            value={discoveryCadence}
            disabled={readOnly || busy}
            onChange={(event) => setDiscoveryCadence(event.target.value)}
          >
            {discoveryOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {es ? (opt.label_es || opt.id) : (opt.label_en || opt.id)}
              </option>
            ))}
          </select>
        </label>
        <label className="agent-settings-panel__field">
          <span>{copy.cadence}</span>
          <select
            value={solutionCadence}
            disabled={readOnly || busy}
            onChange={(event) => setSolutionCadence(event.target.value)}
          >
            {cadenceOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {es ? (opt.label_es || opt.id) : (opt.label_en || opt.id)}
              </option>
            ))}
          </select>
        </label>
        <div
          className={`agent-settings-panel__folders${focusSection === 'sources' ? ' is-focus' : ''}`}
          role="group"
          aria-labelledby="agent-folders-title"
          ref={(node) => { sourcesRef.current = node; }}
        >
          <strong id="agent-folders-title">{copy.foldersTitle}</strong>
          <p className="agent-settings-panel__help">{copy.foldersHelp}</p>
          <label className="agent-settings-panel__folder-row agent-settings-panel__folder-all">
            <input
              type="checkbox"
              checked={allFoldersSelected}
              ref={(node) => {
                if (node) node.indeterminate = someFoldersSelected && !allFoldersSelected;
              }}
              disabled={readOnly || busy || allFolderKeys.length === 0}
              onChange={() => toggleAllFolders()}
            />
            <span>
              <strong>{allFoldersSelected ? copy.clearAllFolders : copy.selectAllFolders}</strong>
            </span>
          </label>
          <ul className="agent-settings-panel__folder-list">
            {folderPresets.map((preset) => {
              const checked = rootPaths.has(String(preset.path || '').trim().toLowerCase());
              const label = es
                ? (preset.label_es || preset.label_en || preset.path)
                : (preset.label_en || preset.label_es || preset.path);
              return (
                <li key={preset.id || preset.path}>
                  <label className="agent-settings-panel__folder-row">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={readOnly || busy}
                      onChange={() => togglePreset(preset)}
                    />
                    <span>
                      <strong>{label}</strong>
                      <em>{preset.path}</em>
                    </span>
                  </label>
                </li>
              );
            })}
            {customFolders.map((row) => (
                <li key={`custom-${row.path}`}>
                  <label className="agent-settings-panel__folder-row">
                    <input
                      type="checkbox"
                      checked
                      disabled={readOnly || busy}
                      onChange={() => togglePreset(row)}
                    />
                    <span>
                      <strong>{row.path}</strong>
                      <em>{es ? 'Personalizada' : 'Custom'}</em>
                    </span>
                  </label>
                </li>
              ))}
          </ul>
          {!readOnly ? (
            <div className="agent-settings-panel__folder-add">
              <input
                type="text"
                value={customRoot}
                disabled={busy || browseBusy}
                placeholder={copy.folderPlaceholder}
                aria-label={copy.foldersTitle}
                onChange={(event) => setCustomRoot(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addCustomFolder();
                  }
                }}
              />
              <button
                type="button"
                className="dashboard-btn"
                disabled={busy || browseBusy || !customRoot.trim()}
                onClick={() => addCustomFolder()}
              >
                {copy.addFolder}
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn-primary"
                disabled={busy || browseBusy}
                title={copy.browseNeedAgent}
                onClick={() => void browseFolder()}
              >
                {browseBusy ? copy.browsingFolder : copy.browseFolder}
              </button>
            </div>
          ) : null}
          {foldersDirty ? (
            <p className="agent-settings-panel__folders-dirty" role="status">
              {copy.foldersUnsaved}
            </p>
          ) : null}
        </div>
        {!readOnly ? (
          <div className="agent-settings-panel__actions">
            <button type="button" className="dashboard-btn dashboard-btn-primary" disabled={busy} onClick={() => void saveCadence()}>
              {copy.saveCadence}
            </button>
          </div>
        ) : null}
      </section>
      ) : null}

      {draftsOnly ? (
      <section
        className="agent-settings-panel__block is-focus"
        aria-labelledby="agent-drafts-title"
        ref={(node) => { draftsRef.current = node; }}
      >
        <div className="agent-settings-panel__block-head">
          <h4 id="agent-drafts-title">{copy.drafts}</h4>
          {awaiting.length ? <span className="agent-settings-panel__badge">{awaiting.length}</span> : null}
        </div>
        <p className="agent-settings-panel__help">
          <DodTermText text={copy.draftsHelp} language={language} seen={new Set()} />
        </p>
        {awaiting.length ? (
          <ul className="agent-settings-panel__list">
            {awaiting.map((run) => {
              const proposals = run.draft?.proposals || [];
              const taskId = String(run.draft?.openTask || '').trim();
              const canOpenTask = Boolean(taskId && taskId !== 'person-reviews-smart-solutions' && onOpenTask);
              return (
                <li key={run.run_id}>
                  <strong>
                    <DodTermText text={run.title || run.skill} language={language} seen={new Set()} />
                  </strong>
                  <p>
                    <DodTermText text={run.summary || ''} language={language} seen={new Set()} />
                  </p>
                  {proposals.length ? (
                    <div className="agent-settings-panel__proposals">
                      <span className="agent-settings-panel__proposals-label">{copy.proposals}</span>
                      <ul>
                        {proposals.slice(0, 5).map((item, idx) => (
                          <li key={item.id || `${run.run_id}-p-${idx}`}>
                            <strong>{item.title}</strong>
                            {item.action ? (
                              <span>
                                {' — '}
                                {item.action}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {!readOnly ? (
                    <div className="agent-settings-panel__actions">
                      {canOpenTask ? (
                        <button
                          type="button"
                          className="dashboard-btn dashboard-btn-primary"
                          disabled={busy}
                          onClick={() => {
                            onOpenTask?.(taskId);
                            onClose();
                          }}
                        >
                          {copy.openTask}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={canOpenTask ? 'dashboard-btn' : 'dashboard-btn dashboard-btn-primary'}
                        disabled={busy}
                        onClick={() => void decide(run.run_id, 'approved')}
                      >
                        {copy.approve}
                      </button>
                      <button
                        type="button"
                        className="dashboard-btn"
                        disabled={busy}
                        onClick={() => void decide(run.run_id, 'rejected')}
                      >
                        {copy.reject}
                      </button>
                      <button
                        type="button"
                        className="dashboard-btn"
                        disabled={busy}
                        onClick={() => void decide(run.run_id, 'dismissed')}
                      >
                        {copy.dismiss}
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="agent-settings-panel__help">{copy.draftsEmpty}</p>
        )}
      </section>
      ) : null}

      {showSchedule && catalog.length ? (
        <details
          className="agent-settings-panel__fold"
          open={gatesOpen}
          onToggle={(event) => setGatesOpen(event.currentTarget.open)}
        >
          <summary>{copy.gates}</summary>
          <ul className="agent-settings-panel__list">
            {catalog.map((entry) => {
              const modes = entry.modes?.length ? entry.modes : ['off', 'on_approval'];
              const label = es ? (entry.label_es || entry.id) : (entry.label_en || entry.id);
              return (
                <li key={entry.id}>
                  <strong>
                    <DodTermText text={label} language={language} seen={new Set()} />
                  </strong>
                  <label className="agent-settings-panel__field">
                    <span className="visually-hidden">{label}</span>
                    <select
                      aria-label={label}
                      disabled={readOnly || busy}
                      value={permissions[entry.id] || 'off'}
                      onChange={(event) => setPermissions((current) => ({
                        ...current,
                        [entry.id]: event.target.value,
                      }))}
                    >
                      {modes.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode === 'auto' ? copy.modeAuto : mode === 'on_approval' ? copy.modeApproval : copy.modeOff}
                        </option>
                      ))}
                    </select>
                  </label>
                </li>
              );
            })}
          </ul>
          {!readOnly ? (
            <div className="agent-settings-panel__actions">
              <button type="button" className="dashboard-btn dashboard-btn-primary" disabled={busy} onClick={() => void saveGates()}>
                {copy.saveGates}
              </button>
            </div>
          ) : null}
        </details>
      ) : null}

      {showSchedule ? (
      <div className="agent-settings-panel__links">
        <a className="dashboard-btn" href="/console/agent">{copy.install}</a>
        <a className="dashboard-btn" href="/console/agent?view=scope">{copy.scope}</a>
        <a className="dashboard-btn" href="/console/agent?view=connect2ai">
          {es ? 'Connect2AI' : 'Connect2AI'}
        </a>
      </div>
      ) : null}
    </div>
  );

  if (typeof document === 'undefined') return panel;
  return createPortal(panel, document.body);
}
